import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface AnuncioData {
  activo: boolean;
  texto: string;
  subtexto: string;
}

const LAMBORGHINI_IMG = 'https://public.readdy.ai/ai/img_res/fff308bd2e2f8e89a79e02fe9f1320e8.jpg';

const CheckerStripe = () => (
  <div className="h-2 bg-gradient-to-r from-yellow-500 via-yellow-300 to-yellow-500 relative overflow-hidden flex-shrink-0">
    <div className="absolute inset-0 flex">
      {[...Array(24)].map((_, i) => (
        <div key={i} className={`flex-1 h-full ${i % 2 === 0 ? 'bg-black/25' : ''}`} />
      ))}
    </div>
  </div>
);

const AnuncioWidget = () => {
  const [anuncio, setAnuncio] = useState<AnuncioData | null>(null);
  const [appeared, setAppeared] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data: row } = await supabase
        .from('TALLERMECANICOKEVIN_settings')
        .select('value')
        .eq('key', 'anuncio')
        .maybeSingle();
      if (row?.value) {
        const d = row.value as AnuncioData;
        setAnuncio(d);
        if (d.activo) {
          setTimeout(() => setAppeared(true), 1500);
        }
      }
    };
    load();
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setClosing(false);
    }, 300);
  };

  if (!anuncio || !anuncio.activo) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-3">

      {/* ── Tarjeta expandida ── */}
      {isOpen && (
        <div
          className={`transition-all duration-300 origin-bottom-right ${
            !closing
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-90 translate-y-4'
          }`}
          style={{ width: '300px', maxWidth: 'calc(100vw - 3rem)' }}
        >
          <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-yellow-400/70">
            <CheckerStripe />

            {/* Imagen + texto */}
            <div
              className="relative h-52 bg-black flex items-center justify-center overflow-hidden cursor-pointer"
              style={{
                backgroundImage: `url(${LAMBORGHINI_IMG})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/20" />

              {/* Bandera cuadros izquierda */}
              <div className="absolute top-3 left-3 flex gap-0.5 opacity-70">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className={`w-2 h-2 ${i % 2 === 0 ? 'bg-white' : 'bg-black'}`} />
                ))}
              </div>
              {/* Bandera cuadros derecha */}
              <div className="absolute top-3 right-10 flex gap-0.5 opacity-70">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className={`w-2 h-2 ${i % 2 === 0 ? 'bg-white' : 'bg-black'}`} />
                ))}
              </div>

              {/* Badge ANUNCIO */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2">
                <span className="bg-yellow-400 text-black text-[9px] font-black px-3 py-0.5 rounded-full tracking-widest uppercase">
                  ANUNCIO
                </span>
              </div>

              {/* Botón cerrar */}
              <button
                onClick={handleClose}
                className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-black/80 border border-zinc-600 flex items-center justify-center cursor-pointer hover:border-yellow-400/60 hover:bg-zinc-900 transition-all z-20"
                aria-label="Cerrar anuncio"
              >
                <i className="ri-close-line text-zinc-300 text-xs" />
              </button>

              {/* Texto */}
              <div className="relative z-10 px-5 text-center mt-5">
                <p className="text-white font-black text-sm leading-snug drop-shadow-lg">
                  {anuncio.texto}
                </p>
                {anuncio.subtexto && (
                  <p className="text-yellow-400 text-xs font-bold mt-2 tracking-wide">
                    {anuncio.subtexto}
                  </p>
                )}
              </div>
            </div>

            <CheckerStripe />
          </div>
        </div>
      )}

      {/* ── Botón flotante (siempre visible cuando hay anuncio activo) ── */}
      <button
        onClick={isOpen ? handleClose : handleOpen}
        className={`relative w-14 h-14 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black shadow-2xl flex items-center justify-center cursor-pointer transition-all duration-300 ${
          appeared ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
        } ${isOpen ? 'rotate-12' : 'hover:scale-110'}`}
        aria-label="Ver anuncio"
      >
        {/* Pulso animado cuando está cerrado */}
        {!isOpen && (
          <span className="absolute inset-0 rounded-full bg-yellow-400 animate-ping opacity-40" />
        )}
        <div className="w-14 h-14 flex items-center justify-center">
          <i className={`text-xl transition-all duration-300 ${isOpen ? 'ri-close-line' : 'ri-megaphone-line'}`} />
        </div>
      </button>

    </div>
  );
};

export default AnuncioWidget;
